import { GeckoFeatureModelType } from 'types/assetModel/assetModelEnums'
import { installationDateFormField } from 'types/assetModel/commonFields'
import {
  FeatureTableOptions,
  GeckoEditableFeature,
} from 'types/assetModel/features/baseClasses/geckoEditableFeature'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { parseDate } from 'utils/parsingHelpers'
import {
  optionalNumberSchema,
  requiredNumberSchema,
  requiredStringSchema,
} from 'utils/zodHelpers'

export interface GeckoConicalPlateJSON {
  type: GeckoFeatureModelType.ConePlate
  name: string
  feature_geometry: {
    x_start: number //in degrees
    x_end: number
    y_start: number
    y_end: number
  }
  surface_specs: {
    wall_nominal_thickness?: number
    coating_nominal_thickness?: number
  }
  installation_date?: string
}

export const NewGeckoConicalPlateJSON: GeckoConicalPlateJSON = {
  type: GeckoFeatureModelType.ConePlate,
  name: 'New Plate',
  feature_geometry: {
    x_start: 0,
    x_end: 0,
    y_start: 0,
    y_end: 0,
  },
  surface_specs: {},
}

export class GeckoConicalPlate extends GeckoEditableFeature {
  readonly name: string
  readonly type = GeckoFeatureModelType.ConePlate
  readonly geometry: GeckoComponentRect
  readonly wallNominalThickness: GeckoMeasurement | undefined
  readonly coatingNominalThickness: GeckoMeasurement | undefined
  readonly installationDate: Date | undefined

  constructor(
    id: string,
    name: string,
    geometry: GeckoComponentRect,
    wallNominal: GeckoMeasurement | undefined,
    coatingNominal: GeckoMeasurement | undefined,
    installationDate: Date | undefined
  ) {
    super(id)
    this.name = name
    this.geometry = geometry
    this.wallNominalThickness = wallNominal
    this.coatingNominalThickness = coatingNominal
    this.installationDate = installationDate
  }

  public get isXWrapped() {
    return this.geometry.x.start.num >= this.geometry.x.end.num
  }

  public static CreateFromJSON(
    componentID: string,
    plateID: string,
    plateJSON: GeckoConicalPlateJSON
  ): GeckoConicalPlate {
    const xDimensionMeasurementUnits = GeckoMeasurementUnit.Degrees
    const yDimensionMeasurementUnits = GeckoMeasurementUnit.Meters
    const thicknessMeasurementUnits = GeckoMeasurementUnit.Meters

    const featureGeometryData = plateJSON.feature_geometry as Record<
      string,
      number
    >
    const plateGeometry = GeckoComponentRect.CreateFromXYs(
      componentID as string,
      featureGeometryData.x_start as number,
      featureGeometryData.y_start as number,
      featureGeometryData.x_end as number,
      featureGeometryData.y_end as number,
      xDimensionMeasurementUnits,
      yDimensionMeasurementUnits
    )

    const surfaceSpecs = plateJSON.surface_specs as Record<string, number>

    const wallNominalThickness = surfaceSpecs.wall_nominal_thickness
      ? GeckoMeasurement.CreateFromValue(
          surfaceSpecs.wall_nominal_thickness as number,
          thicknessMeasurementUnits
        )
      : undefined
    const coatingNominalThickness = surfaceSpecs.coating_nominal_thickness
      ? GeckoMeasurement.CreateFromValue(
          surfaceSpecs.coating_nominal_thickness as number,
          thicknessMeasurementUnits
        )
      : undefined

    const installationDate = parseDate(plateJSON.installation_date)
    const plateName = plateJSON.name as string

    return new GeckoConicalPlate(
      plateID,
      plateName,
      plateGeometry,
      wallNominalThickness,
      coatingNominalThickness,
      installationDate
    )
  }

  public static CreateMapFromJSON(
    componentID: string,
    recordsJSON: Record<string, GeckoConicalPlateJSON>
  ): Map<string, GeckoConicalPlate> {
    return Object.keys(recordsJSON).reduce((resultObj, plateID) => {
      resultObj.set(
        plateID,
        GeckoConicalPlate.CreateFromJSON(
          componentID,
          plateID,
          recordsJSON[plateID]
        )
      )
      return resultObj
    }, new Map<string, GeckoConicalPlate>())
  }

  asJSON() {
    return generateConicalPlate(
      this.name,
      this.geometry,
      this.wallNominalThickness,
      this.coatingNominalThickness,
      this.installationDate
    )
  }
}

export function generateConicalPlate(
  name: string,
  geometry: GeckoComponentRect,
  wallNominalThickness?: GeckoMeasurement,
  coatingNominalThickness?: GeckoMeasurement,
  installation_date?: Date
): GeckoConicalPlateJSON {
  const geometryInMeters = geometry.convertTo(
    GeckoMeasurementUnit.Degrees,
    GeckoMeasurementUnit.Meters
  )
  const plateJSON: GeckoConicalPlateJSON = {
    type: GeckoFeatureModelType.ConePlate,
    name: name,
    feature_geometry: {
      x_start: geometryInMeters.x.start.num,
      y_start: geometryInMeters.y.start.num,
      x_end: geometryInMeters.x.end.num,
      y_end: geometryInMeters.y.end.num,
    },
    surface_specs: {},
  }
  if (wallNominalThickness) {
    plateJSON.surface_specs.wall_nominal_thickness =
      wallNominalThickness.convertTo(GeckoMeasurementUnit.Meters).num
  }
  if (coatingNominalThickness) {
    plateJSON.surface_specs.coating_nominal_thickness =
      coatingNominalThickness.convertTo(GeckoMeasurementUnit.Meters).num
  }

  if (installation_date) {
    plateJSON.installation_date = installation_date.toISOString().split('T')[0]
  }

  return plateJSON
}

export const ConicalPlateFormFields = (plateJSON: GeckoConicalPlateJSON) => {
  return [
    {
      dataKey: 'name',
      label: 'Plate Name',
      value: plateJSON.name,
      unit_magnitude: null,
      initialValue: 'Plate',
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    installationDateFormField(plateJSON.installation_date, false),
    {
      dataKey: 'wall_nominal_thickness',
      label: 'Wall Nominal Thickness',
      value: plateJSON.surface_specs.wall_nominal_thickness,
      unit_magnitude: UnitMagnitude.InchesOrMillimeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'coating_nominal_thickness',
      label: 'Coating Nominal Thickness',
      value: plateJSON.surface_specs.coating_nominal_thickness,
      unit_magnitude: UnitMagnitude.InchesOrMillimeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'x_start',
      label: 'X Start',
      value: plateJSON.feature_geometry.x_start,
      unit_magnitude: null,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'x_end',
      label: 'X End',
      value: plateJSON.feature_geometry.x_end,
      unit_magnitude: null,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'y_start',
      label: 'Y Start',
      value: plateJSON.feature_geometry.y_start,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'y_end',
      label: 'Y End',
      value: plateJSON.feature_geometry.y_end,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
  ]
}

export const ConicalPlateJSONFromFormFields = (formFields: FormField[]) => {
  return generateConicalPlate(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoComponentRect.CreateFromXYs(
      'fake_component_id',
      getFormFieldByDataKey(formFields, 'x_start')?.value as number,
      getFormFieldByDataKey(formFields, 'y_start')?.value as number,
      getFormFieldByDataKey(formFields, 'x_end')?.value as number,
      getFormFieldByDataKey(formFields, 'y_end')?.value as number,
      GeckoMeasurementUnit.Degrees,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'wall_nominal_thickness')
        ?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'coating_nominal_thickness')
        ?.value as number,
      GeckoMeasurementUnit.Meters
    ),

    new Date(
      getFormFieldByDataKey(formFields, 'installation_date')?.value as string
    )
  )
}

export const ConePlateFeatureTableOptions: FeatureTableOptions = {
  type: GeckoFeatureModelType.ConePlate,
  title: 'Plates',
  singularName: 'Plate',
  fields: ['x_start', 'x_end', 'y_start', 'y_end'],
  sortBy: [{ fieldName: 'y_start', order: 'asc' }],
}
